import React from 'react';
import { Button, Typography, makeStyles, Box } from '@material-ui/core';
import _get from 'lodash/get';
import _classNames from 'classnames';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { getImageFile, replacePlaceholders } from 'utils/stringUtils';
import { getTakenCourses } from 'containers/Course/selectors';
import Badge from 'components/Badge';
import useSiteCopySelector from 'components/useSiteCopySelector';
import Image from 'components/ResourceItemSmall/components/Image';
import ResourceItemTitle from 'components/ResourceItemSmall/components/Title';
import { checkCourseCompletion } from 'containers/Course/utils';
import Mixpanel from 'utils/mixpanelService';
import useCourseProgress from './hooks/useCourseProgress';

const useStyles = makeStyles(theme => ({
  card: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: 271,
    borderRadius: 5,
    overflow: 'hidden',
    position: 'relative',
    color: theme.palette.primary.main,
    cursor: 'pointer',
    border: '1px solid #E9ECED',
    backgroundColor: '#fff',
  },
  countBadge: {
    display: 'inline-flex',
    inset: '15px auto auto 15px',
    position: 'absolute',
    borderRadius: '16px',
    backgroundColor: '#ffffff',
    '& span': {
      ...theme.typography.pxSmall,
      padding: '4px 8px',
      color: '#232221',
    },
  },
  infoContainer: {
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    padding: props =>
      props.resourceCard ? '2px 9px 12px 14px' : '10px 20px 55px',
  },
  resourcePageContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginRight: 5,
  },
  truncate: {
    display: '-webkit-box',
    '-webkit-line-clamp': '2',
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  plainCardBody: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    padding: '12px',
  },
  plainCardRightContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  completedBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 5px',
    borderRadius: '10px',
    fontSize: '0.75rem',
    color: theme.palette.text.white,
    backgroundColor: '#37BB7C',
  },
  plainCardTitle: {
    fontSize: '1.125rem',
    lineHeight: '1.5rem',
    textAlign: 'center',
  },
  plainCardResourcesCount: {
    textAlign: 'center',
    ...theme.typography.pxSmall,
  },
  imageContainer: {
    width: 80,
    height: 80,
    margin: '8px auto',
    borderRadius: 10,
    overflow: 'hidden',

    '& > img': {
      maxWidth: '100%',
      height: '100%',
      objectFit: 'cover',
    },
  },
  plainCardButton: {
    alignSelf: 'center',
    margin: 'auto 0px 20px',
    borderRadius: 5,
    fontSize: '0.75rem',
    fontWeight: 700,
    lineHeight: '1.125rem',
  },
  progress: {
    height: 6,
    flexShrink: 0,
    backgroundColor: '#ECF4F8',

    '&:before': {
      content: "''",
      display: 'block',
      height: '100%',
      width: props => `${props.progress}%`,
      backgroundColor: '#37BB7C',
    },
  },
  imageWrapper: {
    height: 123,
  },
}));

const CourseItem = ({
  item,
  onClick,
  resourceCard,
  route = '/courses',
  trackingPath = 'courses',
  trackingProps = {},
}) => {
  const { title, name, slug, id, modules } = item;
  const history = useHistory();

  const finalTitle = title || name;
  const backgroundImage = getImageFile(item);
  const takenCourses = useSelector(getTakenCourses);
  const [coursesSiteCopy, seriesSiteCopy] = useSiteCopySelector([
    'courses',
    'series',
  ]);

  const taken = takenCourses[id];
  const modulesLength = _get(modules, 'length', 0);
  const completedModulesIds = _get(taken, 'completedModulesIds', []);
  const isCompleted =
    taken && checkCourseCompletion(modules, completedModulesIds);
  const completedModuleCount = isCompleted
    ? modulesLength
    : modules.filter(m => completedModulesIds.includes(m.sys.id)).length;

  const progress = useCourseProgress(item);

  const isStartedAndNotCompleted = !isCompleted && _get(taken, 'enrolled');

  const classes = useStyles({
    backgroundImage,
    progress,
    resourceCard,
  });

  const countBadge = !!completedModuleCount && (
    <Box className={classes.countBadge}>
      <span>
        {replacePlaceholders(_get(coursesSiteCopy, 'pageCopy.modulesCount'), {
          '#count': modulesLength,
        })}
      </span>
    </Box>
  );

  const to = {
    pathname: `${route}/${slug}`,
    state: {
      trackingProps: {
        path: trackingPath,
      },
    },
  };

  const onNavigate = e => {
    e.preventDefault();
    const pageName = history.location.pathname.split('/')[1];
    const finalPageName = pageName ? `${pageName}Page` : 'homePage';
    Mixpanel.track('Resource Card - Clicked', {
      name: title,
      type: 'courses',
      page: finalPageName,
      path: pageName,
      slug,
      entryId: id,
      ...trackingProps,
    });

    history.push(to);
    if (onClick) onClick();
  };

  if (resourceCard) {
    return (
      <a href={to.pathname} onClick={onNavigate} className={classes.card}>
        <Box className={classes.progress} />
        <Box className={classes.imageWrapper}>
          <Image file={backgroundImage} type="course" />
          {countBadge}
        </Box>
        <Box className={classes.infoContainer}>
          <Box className={classes.resourcePageContainer}>
            <Badge removeMargin type="courses" />
          </Box>
          <ResourceItemTitle title={finalTitle} />
        </Box>
      </a>
    );
  }

  const actionLabel = isStartedAndNotCompleted
    ? _get(seriesSiteCopy, 'pageCopy.continueCTA')
    : isCompleted
    ? _get(seriesSiteCopy, 'pageCopy.viewResources')
    : _get(seriesSiteCopy, 'pageCopy.startCTA');

  return (
    <Box onClick={onNavigate} className={classes.card}>
      <Box className={classes.progress} />
      <Box className={classes.plainCardBody}>
        <Box display="flex" justifyContent="space-between">
          <Badge customClasses={classes} removeMargin type="course" />
          <Box className={classes.plainCardRightContainer}>
            {isCompleted && (
              <span className={classes.completedBadge}>
                {_get(seriesSiteCopy, 'pageCopy.completedCTA')}
              </span>
            )}
          </Box>
        </Box>
        <Box className={classes.imageContainer}>
          <img src={backgroundImage} alt="Courses" />
        </Box>
        <Typography
          className={_classNames(classes.plainCardTitle, classes.truncate)}
          variant="h5"
          color="textPrimary"
        >
          {finalTitle}
        </Typography>
        <Typography
          className={classes.plainCardResourcesCount}
          color="textPrimary"
        >
          {replacePlaceholders(_get(coursesSiteCopy, 'pageCopy.modulesCount'), {
            '#count': modulesLength,
          })}
        </Typography>
        <Button
          className={classes.plainCardButton}
          variant="contained"
          color="primary"
          aria-label={`${finalTitle} ${actionLabel}`}
        >
          {actionLabel}
        </Button>
      </Box>
    </Box>
  );
};

export default CourseItem;
